import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    labels: Array,
    pastStock: Array,
    futureStock: Array,
    minStock: Number,
    todayIndex: Number,
    events: Array,
    unit: String
  }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    const ctx = this.element.getContext('2d')

    // 과거 재고 데이터 (오늘까지)
    const pastData = this.pastStockValue.map((val, idx) =>
      idx <= this.todayIndexValue ? val : null
    )

    // 미래 예상 재고 데이터 (오늘부터)
    const futureData = this.labelsValue.map((_, idx) => {
      if (idx < this.todayIndexValue) return null
      const futureIdx = idx - this.todayIndexValue
      return this.futureStockValue[futureIdx]
    })

    // 최소재고 기준선
    const minStockData = this.labelsValue.map(() => this.minStockValue)

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.labelsValue,
        datasets: [
          {
            label: '실제재고',
            data: pastData,
            borderColor: '#3182F6',
            backgroundColor: 'rgba(49, 130, 246, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointRadius: 3,
            pointHoverRadius: 5
          },
          {
            label: '예상재고',
            data: futureData,
            borderColor: '#3182F6',
            backgroundColor: 'rgba(49, 130, 246, 0.1)',
            borderWidth: 2,
            borderDash: [5, 5],
            fill: false,
            tension: 0.1,
            pointRadius: 3,
            pointHoverRadius: 5
          },
          {
            label: '최소재고',
            data: minStockData,
            borderColor: '#EF4444',
            borderWidth: 1,
            borderDash: [3, 3],
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                family: "'Pretendard', sans-serif",
                size: 12
              }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleColor: '#111827',
            bodyColor: '#374151',
            borderColor: '#E5E7EB',
            borderWidth: 1,
            padding: 12,
            displayColors: false,
            filter: (tooltipItem) => {
              // 최소재고 라인은 툴팁에서 제외
              return tooltipItem.dataset.label !== '최소재고'
            },
            callbacks: {
              title: (items) => {
                const idx = items[0].dataIndex
                const isToday = idx === this.todayIndexValue
                return isToday ? `${items[0].label} (오늘)` : items[0].label
              },
              label: (context) => {
                const value = context.parsed.y
                if (value === null || value === undefined) return null
                const unit = this.unitValue || '개'
                const label = context.dataset.label
                return `🟦 ${label} ${Math.round(value)}${unit}`
              },
              afterBody: (items) => {
                const idx = items[0].dataIndex
                const event = this.eventsValue[idx]
                if (!event) return []

                const lines = []
                const unit = this.unitValue || '개'

                if (event.stock_in > 0) {
                  lines.push(`🟩 입고 ${Math.round(event.stock_in)}${unit}`)
                }
                if (event.stock_out > 0) {
                  lines.push(`🟥 출고 ${Math.round(event.stock_out)}${unit}`)
                }
                if (event.expected_out > 0) {
                  lines.push(`🟧 예상출고 ${Math.round(event.expected_out)}${unit}`)
                }

                return lines.length > 0 ? ['', ...lines] : []
              }
            }
          },
          annotation: {
            annotations: {
              todayLine: {
                type: 'line',
                xMin: this.todayIndexValue,
                xMax: this.todayIndexValue,
                borderColor: '#9CA3AF',
                borderWidth: 1,
                borderDash: [2, 2]
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                family: "'Pretendard', sans-serif",
                size: 11
              },
              maxRotation: 45,
              minRotation: 45
            }
          },
          y: {
            beginAtZero: false,
            grid: {
              color: '#F3F4F6'
            },
            ticks: {
              font: {
                family: "'Pretendard', sans-serif",
                size: 11
              }
            }
          }
        }
      }
    })
  }
}
